import type { RouterOutput } from "@lattice/backend/trpc";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@lattice/frontend/components/ui/table";
import { Tooltip } from "@lattice/frontend/components/ui/tooltip";
import { format } from "date-fns";
import { CircleQuestionMarkIcon } from "lucide-react";
import Link from "next/link";

import { routes } from "@lattice/frontend/lib/routes/routes";

export default function StockPoliticianTradesTable({
  data,
}: {
  data: RouterOutput["stock"]["get_congressman_trading"];
}) {
  const data_grouped = Object.groupBy(
    data,
    ({ TransactionDate: transactionDate }) => transactionDate.toISOString(),
  );

  return (
    <>
      <div className="rounded-[2px] border border-primary/30 relative max-w-full max-h-full">
        <Table
          className="text-xs sm:text-sm md:text-base"
          containerClass="overflow-y-auto max-h-[600px]"
        >
          <TableHeader className="bg-primary/20 backdrop-blur-sm">
            <TableRow className="border-b-2 border-primary/30 hover:bg-transparent">
              {[
                "Transaction Date",
                "Purchase/Sale",
                "Name",
                "Amount",
                "Party",
                "House",
              ].map((header) => (
                <TableHead
                  className="text-xs font-semibold text-foreground uppercase tracking-wider pl-5"
                  key={header}
                >
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.keys(data_grouped)
              .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
              .map((date) => {
                const items = data_grouped[date];

                if (!items) return null;

                return items.map((item) => {
                  return (
                    <TableRow
                      key={item.id}
                      className={`${item.Transaction?.includes("Purchase") ? "bg-green-500/15 border-l-2 border-l-green-500/70" : item.Transaction?.includes("Sale") ? "bg-red-500/15 border-l-2 border-l-red-500/70" : ""}`}
                    >
                      <TableCell className="pl-5">
                        <div className="">
                          <span>
                            {format(item.TransactionDate, "MMM dd, yyyy")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>{item.Transaction || "--"}</TableCell>
                      <TableCell className="flex items-center gap-2">
                        {item.Representative}
                        <Tooltip content="Ask Prism">
                          <Link
                            prefetch={false}
                            href={routes.shellPrompt(
                              `Who is ${item.Representative}`,
                            )}
                          >
                            <CircleQuestionMarkIcon size={16} />
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{item.Amount.toFixed(2)}</TableCell>
                      <TableCell>
                        {item.Party === "R"
                          ? "Republican"
                          : item.Party === "D"
                            ? "Democrat"
                            : !item.Party
                              ? "--"
                              : "Other"}
                      </TableCell>
                      <TableCell>{item.House || "--"}</TableCell>
                    </TableRow>
                  );
                });
              })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
